<script lang="ts">

  import { page } from "$app/state"

  import httpClient from "$lib/axios-client"
  import type { _response } from "$lib/data/type/_response"
  import { Button } from "$lib/components/ui/button"
  import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "$lib/components/ui/dialog"
  import { Label } from "$lib/components/ui/label"
  import { Separator } from "$lib/components/ui/separator"
  import { measureDistance } from "$lib/location"
  import { deviceStore } from "$lib/stores/device-store.svelte"

  import { createMutation, createQuery, useQueryClient } from "@tanstack/svelte-query"
  import { untrack } from "svelte"
  import { toast } from "svelte-sonner"

  import type { Activity } from "./../../_types"
  import TimeTicker from "./time-ticker.svelte"

  type AttendanceType = "start" | "end"

  let {
    open,
    setOpen,
    select,
    callback,
  }: {
    open: boolean
    setOpen: (open: boolean) => void
    select: string
    callback: (activity: Activity) => void
  } = $props()

  const prefix = $derived(page.params.prefix ?? "0")
  const queryClient = useQueryClient()

  let mapElement = $state<HTMLDivElement>()
  let serverError = $state("")

  let position = $derived(deviceStore.geolocation)

  const activity = createQuery(() => ({
    enabled: open && !!select,
    queryKey: ["attendance", prefix, select],
    queryFn: async () => {
      const response = await httpClient.get<_response<Activity>>(`/api/${prefix}/activity/realizations/${select}`)
      return response.data.result!
    },
  }))

  const mutate = createMutation(() => ({
    mutationFn: async (type: AttendanceType) => {
      if (!position || !activity.data) throw new Error("Lokasi belum tersedia")

      const distance = currentDistance()
      if (distance < 0) throw new Error("Jarak tidak valid")
      if (distance > activity.data.customer.radius) throw new Error("Kehadiran diluar zona")

      const response = await httpClient.post<_response<string>>(`/api/sales-mobile/activity/realizations/${select}?attendance`, { _method: "PUT", type, latitude: position.coords.latitude, longitude: position.coords.longitude })

      if (response.status !== 200) throw new Error(response.data.message)

      return response.data.message
    },
    onSuccess: async (message) => {
      toast.success(message)
      await queryClient.invalidateQueries({ queryKey: ["attendance", prefix, select] })
      if (activity.data) callback(activity.data)
      setOpen(false)
    },
    onError: (error: Error) => {
      serverError = error.message
      toast.error("Kehadiran gagal", { description: error.message })
    },
  }))

  const ready = $derived(!!mapElement && !!position)
  const distance = $derived(currentDistance())

  function currentDistance() {

    if (!position || !activity.data?.customer.latitude || !activity.data.customer.longitude) return -1

    return measureDistance(
      position.coords.latitude,
      position.coords.longitude,
      activity.data.customer.latitude,
      activity.data.customer.longitude,
    )
  }

  function handlePresent(type: AttendanceType) {
    mutate.mutate(type)
  }

  function handleDismiss() {
    setOpen(false)
    if (activity.data) callback(activity.data)
  }

  $effect(() => {
    if (open) untrack(() => deviceStore.geolocationRequestUpdate())
  })

  $effect(() => {
    const element = mapElement
    const customer = activity.data?.customer
    if (!open || !element || !position || !customer) return

    const center: [number, number] = [position.coords.latitude, position.coords.longitude]

    let cancelled = false
    let map: { remove: () => void } | undefined
    let frame = 0

    void import("leaflet").then(({ default: Leaflet }) => {
      if (cancelled) return

      const instance = Leaflet.map(element, {
        zoomControl: false,
        dragging: false,
        scrollWheelZoom: false,
      }).setView(center, 17)
      map = instance

      Leaflet.tileLayer("https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", {
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
      }).addTo(instance)
      Leaflet.marker(center).addTo(instance)
      Leaflet.circle([customer.latitude, customer.longitude], {
        color: "#22c55e",
        fillColor: "#22c55e",
        fillOpacity: 0.2,
        radius: customer.radius,
      }).addTo(instance)

      // dialog is still animating in when a cached leaflet chunk resolves immediately; re-measure after layout
      frame = requestAnimationFrame(() => instance.invalidateSize())
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(frame)
      map?.remove()
    }
  })

</script>

<svelte:head>
  <link
    rel="stylesheet"
    href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
</svelte:head>
<Dialog
  open={open}
  onOpenChange={setOpen}>
  <DialogContent
    class="relative w-xs p-3"
    showCloseButton={false}
    onOpenAutoFocus={(event) => event.preventDefault()}>
    <DialogHeader class="hidden">
      <DialogTitle>Kehadiran</DialogTitle>
      <DialogDescription></DialogDescription>
    </DialogHeader>
    {#if activity.isError}
      <div class="p-3 text-destructive">Data aktivitas gagal dimuat</div>
    {/if}
    <form
      action="#"
      autocomplete="off"
      class="space-y-3"
      onsubmit={(event) => event.preventDefault()}>
      <div
        bind:this={mapElement}
        class="isolate h-72 w-full rounded-md bg-muted"
        aria-label="Peta lokasi">
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Mulai</Label>
          <div class="text-desc font-mono">
            {activity.data?.at_start.toDateFormat("HH:mm", "-") || "-"}
          </div>
        </div>
        <div class="flex-1">
          <Label>Selesai</Label>
          <div class="text-desc font-mono">
            {activity.data?.at_end.toDateFormat("HH:mm", "-") || "-"}
          </div>
        </div>
      </div>
      <div class="flex gap-3">
        <div class="flex-1">
          <Label>Radius</Label>
          <div class="text-desc font-mono">
            {activity.data?.customer.radius.toUnitDistance() ?? "-"}
          </div>
        </div>
        <div class="flex-1">
          <Label>Jarak</Label>
          <div class="text-desc font-mono">
            {distance < 0 ? "-" : distance.toUnitDistance()}
          </div>
        </div>
      </div>
      <div class="py-2"></div>
      <div class="flex gap-3">
        <TimeTicker
          activity={activity.data}
          disabled={!ready || mutate.isPending}
          handleSubmitStart={() => handlePresent("start")}
          handleSubmitEnd={() => handlePresent("end")}/>
      </div>
      <Separator/>
      <Button
        type="button"
        variant="outline"
        class="w-full shadow-none"
        disabled={mutate.isPending}
        onclick={handleDismiss}>
        Tutup
      </Button>
    </form>
    {#if serverError}
      <div class="text-sm text-destructive">{serverError}</div>
    {/if}
  </DialogContent>
</Dialog>
